import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import type { AboutInfo } from "@/hooks/usePublicAbout";
import { Link } from "react-router-dom";

interface Props { about: AboutInfo | null; }

export default function HeritageFooter({ about }: Props) {
  const { language } = useLanguage();
  const { brand, localizedName } = useBrand();

  const links = [
    { to: "/", label: language === "ar" ? "الرئيسية" : "Home" },
    { to: "/menu", label: language === "ar" ? "القائمة" : "Menu" },
    { to: "/about", label: language === "ar" ? "من نحن" : "About" },
    { to: "/branches", label: language === "ar" ? "الفروع" : "Branches" },
  ];

  return (
    <footer className="border-t border-border bg-foreground text-background">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {brand.logoType === "image" ? (
                <img src={brand.logo} alt="" className="h-8 w-8 object-contain" />
              ) : (
                <span className="text-2xl">{brand.logo}</span>
              )}
              <span className="font-heading font-bold text-lg text-background">{localizedName(language)}</span>
            </div>
            <p className="text-sm text-background/50 max-w-xs">
              {language === "ar"
                ? "مشويات فاخرة بجودة عالية وتوابل الأصالة"
                : "Premium cuisine with authentic flavors and quality ingredients."}
            </p>
          </div>

          {/* Links */}
          <div className="space-y-3">
            <h4 className="font-heading font-bold text-background/80">
              {language === "ar" ? "روابط سريعة" : "Quick Links"}
            </h4>
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-background/50 hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-3">
            <h4 className="font-heading font-bold text-background/80">
              {language === "ar" ? "تواصل معنا" : "Contact Us"}
            </h4>
            <div className="space-y-2 text-sm text-background/50">
              {about?.contactPhone && <p>{about.contactPhone}</p>}
              {about?.contactEmail && <p>{about.contactEmail}</p>}
              {(language === "ar" ? about?.contactAddressAr || about?.contactAddress : about?.contactAddress) && (
                <p>{language === "ar" ? about?.contactAddressAr || about?.contactAddress : about?.contactAddress}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-3 border-t border-background/10 pt-6 text-center text-sm text-background/40">
          <p>© {new Date().getFullYear()} {localizedName(language)}. {language === "ar" ? "جميع الحقوق محفوظة" : "All rights reserved."}</p>
          <p>
            Powered by <span className="font-semibold text-primary">AlKhawarizmi</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
